import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Subscription } from 'rxjs';
import { ValidateDialogComponent } from 'src/app/dialogs/validate-dialog/validate-dialog.component';
import { ItemContainer } from 'src/app/interfaces/item-container';
import { CNConsultation, CNConsultationType } from 'src/app/models/cnconsultation';
import { CNItem } from 'src/app/models/cnitem';
import { CNPrescription } from 'src/app/models/cnprescription';
import { CNTreatmentKine } from 'src/app/models/cntreatmentkine';
import { ConsultationService } from 'src/app/services/consultation.service';
import { ContactService } from 'src/app/services/contact.service';
import { LegacyContentService } from 'src/app/services/legacy-content.service';
import { PrescriptionService } from 'src/app/services/prescription.service';
import { TreatmentService } from 'src/app/services/treatment.service';
import { UserConfigService } from 'src/app/services/user-config.service';
import { UserOpenItemsService } from 'src/app/services/user-open-items.service';

class ItemComponentMapping {
  public icon: string[];
  public component: string;
}

class ItemComponent {
  public item: CNItem & ItemContainer;
  public typeIcon: string[];
  public modified: boolean;
  public mapping: ItemComponentMapping;
}

@Component({
  selector: 'app-user-open-items',
  templateUrl: './user-open-items.component.html',
  styleUrls: ['./user-open-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserOpenItemsComponent implements OnInit, OnDestroy {

  public items: ItemComponent[] = [];
  public selectedItemIndex: number;

  private itemComponentMapping: Map<string, ItemComponentMapping> = new Map();
  private itemTypeEventSub: Subscription;
  private itemModifiedEventSub: Subscription;
  private itemsSubjectSub: Subscription;

  constructor(private userOpenItemsSrv: UserOpenItemsService, private legacyContent: LegacyContentService,
              private userConfig: UserConfigService, private changeDetection: ChangeDetectorRef,
              private dialog: MatDialog, private contacts: ContactService, private prescriptions: PrescriptionService) {
    // Buld mapping
    this.itemComponentMapping.set('stvaast_consultation', {
      icon: ['fas', 'user-friends'],
      component: 'ContactPanelComponent'}
    );
    this.itemComponentMapping.set('SNItem.CNItem.CNPrescription', {
      icon: ['fas', 'file-prescription'],
      component: 'PrescriptionPanelComponent'}
    );
    // get item type change to update typeIcon
    this.itemTypeEventSub = this.userOpenItemsSrv.itemTypeEvent.subscribe(newType => {
      const openItem = newType[0];
      const openType = newType[1];
      const theItem = this.items.find(itemCompo => itemCompo.item === openItem);
      if (theItem) {
        if (openItem.key === 'stvaast_consultation') {
          (theItem.item as CNConsultation).type = openType;
          theItem.typeIcon = this.contacts.typeIcon(openType) ? this.contacts.typeIcon(openType) : ['fas', 'question'];
          this.changeDetection.markForCheck();
        }
      }
    });
    this.itemModifiedEventSub = this.userOpenItemsSrv.itemModifiedEvent.subscribe(item => {
      this.changeDetection.markForCheck();
    });
  }

  ngOnInit(): void {
    this.itemsSubjectSub = this.userOpenItemsSrv.itemsSubject.subscribe(items => {
      this.items = [];
      items.forEach(item => {
        if (this.itemComponentMapping.has(item.key)) {
          const itemMapping = this.itemComponentMapping.get(item.key);
          let typeIcon: string[] = [];
          if (item.key === 'stvaast_consultation') {
            typeIcon = this.contacts.typeIcon((item as CNConsultation).type) ? this.contacts.typeIcon((item as CNConsultation).type) : ['fas', 'question'];
          }
          this.items.push({
            item,
            typeIcon,
            modified: false,
            mapping: itemMapping
          });
        }
      });
      if (this.items.length !== 0) {
        // select on last item
        this.selectItem(this.items[this.items.length - 1]);
      }
      this.changeDetection.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.itemTypeEventSub.unsubscribe();
    this.itemModifiedEventSub.unsubscribe();
    this.itemsSubjectSub.unsubscribe();
  }

  selectItem(item: ItemComponent) {
    this.legacyContent.SetComponent(item.mapping.component, item.item);
    this.selectedItemIndex = this.items.indexOf(item);
    this.changeDetection.markForCheck();
  }

  closeItem(event, item: ItemComponent) {
    event.stopPropagation();
    if (item.item.key === 'stvaast_consultation') {
      const consultation = item.item as CNConsultation;
      if (consultation.isModified) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
          title: 'Voulez-vous sauvegarder cette consultation ?',
          ok: {
            label: 'Sauvegarder',
            code: 'ok'
          },
          other: {
            label: 'Ne pas sauvegarder',
            code: 'notok'
          },
          cancel: {
            label: 'Annuler',
            code: 'cancel'
          }
        };
        const dialogRef = this.dialog.open(ValidateDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(result => {
          if (result === 'ok') {
            this.contacts.save(consultation);
            this.userOpenItemsSrv.removeItem(item.item);
            // Check if a prescription has been opened and close it
            if (consultation.havePrescription) {
              this.userOpenItemsSrv.removeItem(consultation.prescription);
            }
            this.changeDetection.markForCheck();
          }
          if (result === 'notok') {
            this.userOpenItemsSrv.removeItem(item.item);
            // Check if a prescription has been opened and close it
            if (consultation.havePrescription) {
              this.userOpenItemsSrv.removeItem(consultation.prescription);
            }
            this.changeDetection.markForCheck();
          }
        });
      } else {
        this.userOpenItemsSrv.removeItem(item.item);
        // Check if a prescription has been opened and close it
        if (consultation.havePrescription) {
          this.userOpenItemsSrv.removeItem(consultation.prescription);
        }
        this.changeDetection.markForCheck();
      }
    }
    if (item.item.key === 'SNItem.CNItem.CNPrescription') {
      const prescription = item.item as CNPrescription;
      if (prescription.isModified) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
          title: 'Voulez-vous sauvegarder cette prescription ?',
          ok: {
            label: 'Sauvegarder',
            code: 'ok'
          },
          other: {
            label: 'Ne pas sauvegarder',
            code: 'notok'
          },
          cancel: {
            label: 'Annuler',
            code: 'cancel'
          }
        };
        const dialogRef = this.dialog.open(ValidateDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(result => {
          if (result === 'ok') {
            const updateSub = this.prescriptions.updatePrescription(prescription).subscribe(() => { updateSub.unsubscribe(); });
            this.userOpenItemsSrv.removeItem(item.item);
            this.changeDetection.markForCheck();
          }
          if (result === 'notok') {
            this.userOpenItemsSrv.removeItem(item.item);
            this.changeDetection.markForCheck();
          }
        });
      } else {
        this.userOpenItemsSrv.removeItem(item.item);
        this.changeDetection.markForCheck();
      }
    }
  }

  indexChanged(index: number) {
    if (index !== this.selectedItemIndex) {
      this.selectedItemIndex = index;
      if (this.selectedItemIndex === -1) {
        const patientFirstComponent = this.userConfig.profileValue('CNUser', 'patient_first_component');
        if (patientFirstComponent) {
          this.legacyContent.SetComponent(patientFirstComponent as string);
        } else {
          // clear content
          this.legacyContent.SetComponent(undefined);
        }
      } else {
        // select item
        this.selectItem(this.items[this.selectedItemIndex]);
      }
      console.log('selected index', this.selectedItemIndex);
    }
  }

  itemIconClass(item: ItemComponent) {
    if (item.item.id === 0) {
      return 'icon-new-item-tab';
    }
    if (item.modified || item.item.isModified) {
      return 'icon-modified-item-tab';
    }
    return 'icon-hidden-item-tab';
  }
}
